/**
 * Phase 13 Step 5 — Geometry campaign detail page.
 *
 * Renders:
 *   1. Header (name + status chip + scenario link + actions).
 *   2. SummaryCard.
 *   3. HeatmapCard (Slice Pk Distribution) — driven by the registry.
 *   4. TrajectoryGroupsCard.
 *   5. SliceTable.
 *
 * Reports are STUBBED 501 on the backend for Phase 13, so this page
 * renders a deferred banner instead of a download button.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Download, FileText, Loader2, Square } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import PageHeader from "@/components/PageHeader";
import StatusPill from "@/components/StatusPill";
import HeatmapCard from "@/components/geometry/HeatmapCard";
import SliceTable from "@/components/geometry/SliceTable";
import SummaryCard from "@/components/geometry/SummaryCard";
import TrajectoryGroupsCard from "@/components/geometry/TrajectoryGroupsCard";
import GeometrySweep3D from "@/components/GeometrySweep3D";
import { scrubPhaseLabel } from "@/lib/displayName";
import geometryCampaigns from "@/api/geometryCampaigns";
import { formatApiError } from "@/lib/formatApiError";

const RUNNING_STATES = new Set(["queued", "running"]);

export default function GeometryCampaignDetailPage() {
    const { campaignId } = useParams();
    const navigate = useNavigate();
    const [campaign, setCampaign] = useState(null);
    const [registry, setRegistry] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");
    const [cancelOpen, setCancelOpen] = useState(false);
    const [busy, setBusy] = useState(false);

    /**
     * Phase 14.10 hot-fix — auth-bearing blob download. PM-reported
     * 401 on anchor-href downloads: native anchor navigation skips
     * the axios JWT interceptor (api/client.js:21), so the request
     * arrives with no Authorization header and the backend rejects
     * it. Switching to axios `responseType: "blob"` routes the
     * fetch through the interceptor (Bearer JWT attached), then we
     * synthesise a temp anchor for the browser save dialog.
     */
    const onDownloadReport = async (format) => {
        if (!campaignId) return;
        try {
            const { blob, filename } =
                await geometryCampaigns.downloadReport(campaignId, format);
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            setTimeout(() => URL.revokeObjectURL(url), 500);
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error("[GeometryCampaignDetailPage] report download failed", e);
        }
    };

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                setLoading(true);
                const [c, r] = await Promise.all([
                    geometryCampaigns.get(campaignId),
                    geometryCampaigns.registry(),
                ]);
                if (cancelled) return;
                setCampaign(c);
                setRegistry(r);
            } catch (e) {
                if (!cancelled) setErr(formatApiError(e, "Failed to load campaign"));
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [campaignId]);

    const isRunning = campaign ? RUNNING_STATES.has(campaign.status) : false;

    const onCancelConfirm = async () => {
        setBusy(true);
        try {
            const updated = await geometryCampaigns.cancel(campaignId);
            setCampaign(updated);
            setCancelOpen(false);
        } catch (e) {
            setErr(formatApiError(e, "Failed to cancel campaign"));
        } finally {
            setBusy(false);
        }
    };

    if (loading) {
        return (
            <div
                className="flex items-center justify-center py-24 text-sm text-muted-foreground"
                data-testid="geom-detail-loading"
            >
                <Loader2 className="size-4 animate-spin mr-2" />
                Loading geometry campaign…
            </div>
        );
    }
    if (err && !campaign) {
        return (
            <div
                className="rounded-md border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-200"
                data-testid="geom-detail-error"
            >
                {err}
            </div>
        );
    }
    if (!campaign) return null;

    return (
        <div className="space-y-4" data-testid="geom-detail-page">
            {campaign.legacy && (
                <div
                    className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-amber-200"
                    data-testid="geom-detail-legacy-banner"
                >
                    <strong>LEGACY — read only.</strong> This geometry sweep
                    was created before the unified workbench and is
                    preserved for archival viewing only. Edit, clone, re-run,
                    and delete affordances are disabled. Create a new unified
                    campaign at <Link to="/campaigns/new" className="underline">
                    /campaigns/new</Link>.
                </div>
            )}
            <PageHeader
                title={scrubPhaseLabel(campaign.name)}
                subtitle={
                    <span className="flex items-center gap-2 text-sm">
                        <StatusPill status={campaign.status} />
                        {campaign.scenario_id && (
                            <span className="text-muted-foreground">
                                scenario ·{" "}
                                <Link
                                    to={`/workbench/${campaign.scenario_id}`}
                                    className="hover:underline"
                                    data-testid="geom-detail-scenario-link"
                                >
                                    {campaign.scenario_name || campaign.scenario_id}
                                </Link>
                            </span>
                        )}
                    </span>
                }
                actions={[
                    <Button
                        key="back"
                        variant="outline"
                        size="sm"
                        onClick={() => navigate("/geometry-campaigns")}
                        data-testid="geom-detail-back"
                        className="gap-1"
                    >
                        <ArrowLeft className="size-4" /> Back
                    </Button>,
                    isRunning ? (
                        <Button
                            key="cancel"
                            variant="outline"
                            size="sm"
                            onClick={() => setCancelOpen(true)}
                            data-testid="geom-detail-cancel-button"
                            className="gap-1"
                        >
                            <Square className="size-4" /> Cancel
                        </Button>
                    ) : null,
                ].filter(Boolean)}
            />

            {err && (
                <div
                    className="rounded-md border border-red-500/30 bg-red-500/10 p-2 text-sm text-red-200"
                    data-testid="geom-detail-inline-error"
                >
                    {err}
                </div>
            )}

            <SummaryCard campaign={campaign} />
            <HeatmapCard campaign={campaign} registry={registry} />
            {/* Phase 14.13 Deliverable B — full envelope 3D viewer.
                Mounted only when the campaign is complete + the result
                payload carries slices (the heatmap card uses the same
                gating). */}
            {campaign.status === "complete" && (
                <div
                    className="rounded-md border border-slate-800 bg-slate-900/40 p-3"
                    data-testid="geom-detail-envelope-3d-card"
                >
                    <div className="text-sm font-medium text-slate-200 mb-2">
                        Engagement envelope (3D)
                    </div>
                    <GeometrySweep3D campaign={campaign} />
                </div>
            )}
            <TrajectoryGroupsCard campaign={campaign} />
            <SliceTable campaign={campaign} />

            {/* Phase 14.10 — Report parity with Pk campaigns. PDF
                = Jinja+weasyprint with embedded matplotlib heatmap;
                DOCX = python-docx with parity tables (no chart). Both
                gated on campaign.status === "complete" (the backend
                also enforces this via 409). */}
            {campaign.status === "complete" && (
                <div
                    className="rounded-md border border-emerald-500/30 bg-emerald-500/5 p-3 flex items-start gap-3 flex-wrap"
                    data-testid="geom-detail-reports-panel"
                >
                    <div className="flex-1 min-w-[200px]">
                        <div className="font-medium text-emerald-200 text-sm">
                            Procurement-grade reports ready
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">
                            Headline Pk · slice heatmap · top/bottom envelope
                            cells · failure-mode breakdown · methodology.
                        </div>
                    </div>
                    <Button
                        onClick={() => onDownloadReport("pdf")}
                        data-testid="geom-detail-report-pdf"
                        className="gap-2"
                    >
                        <FileText className="size-4" /> Generate report (PDF)
                    </Button>
                    <Button
                        variant="outline"
                        onClick={() => onDownloadReport("docx")}
                        data-testid="geom-detail-report-docx"
                        className="gap-2"
                    >
                        <Download className="size-4" /> DOCX
                    </Button>
                </div>
            )}

            <AlertDialog open={cancelOpen} onOpenChange={setCancelOpen}>
                <AlertDialogContent data-testid="geom-detail-cancel-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Cancel this geometry campaign?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Any points already simulated will be preserved in the Slice Pk table, but no further points will be run.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="geom-detail-cancel-no">
                            Keep running
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={onCancelConfirm}
                            disabled={busy}
                            data-testid="geom-detail-cancel-yes"
                        >
                            {busy ? "Cancelling…" : "Cancel campaign"}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
